import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true
});

export const authAPI = {
  me: () => api.get('/auth/me'),
  login: (email, password, rememberMe) => api.post('/auth/login', { email, password, remember_me: rememberMe }),
  register: (email, password, username) => api.post('/auth/register', { email, password, username }),
  logout: () => api.post('/auth/logout'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword })
};

export const statementAPI = {
  downloadCSV: () => api.get('/user/statement/csv', { responseType: 'blob' }),
  downloadPDF: () => api.get('/user/statement/pdf', { responseType: 'blob' })
};

export const userAPI = {
  getWallets: () => api.get('/user/wallets'),
  getTransactions: (skip = 0, limit = 50) => api.get(`/user/transactions?skip=${skip}&limit=${limit}`),
  getDirectory: (search = '', skip = 0, limit = 50) => api.get(`/user/directory?search=${encodeURIComponent(search)}&skip=${skip}&limit=${limit}`),
  deposit: (voucherCode, voucherType, currency = null, amount = null, providerId = null) => api.post('/user/deposit', { voucher_code: voucherCode, voucher_type: voucherType, currency, amount, provider_id: providerId }),
  withdraw: (data) => api.post('/user/withdraw', data),
  swap: (fromCurrency, toCurrency, amount) => api.post('/user/swap', { from_currency: fromCurrency, to_currency: toCurrency, amount }),
  transfer: (recipientEmail, currency, amount) => api.post('/user/transfer', { recipient_email: recipientEmail, currency, amount }),
  getVouchers: () => api.get('/user/vouchers'),
  getOrders: (skip = 0, limit = 50) => api.get(`/user/orders?skip=${skip}&limit=${limit}`),
  createOrder: (items) => api.post('/user/orders', { items }),
  getNotifications: (unreadOnly = false) => api.get(`/user/notifications?unread_only=${unreadOnly}`),
  updateProfile: (data) => api.put('/user/profile', data),
  uploadProfilePicture: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/user/upload-profile-picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getPublicProfile: (handle) => api.get(`/user/profile/${handle}`),
  follow: (userId) => api.post(`/user/follow/${userId}`),
  unfollow: (userId) => api.delete(`/user/follow/${userId}`),
  getFollowers: () => api.get('/user/followers'),
  getFollowing: () => api.get('/user/following'),
  getContacts: () => api.get('/user/contacts'),
  addContact: (contactUserId, nickname = null) => api.post('/user/contacts', { contact_user_id: contactUserId, nickname }),
  
  // Agent Network - Customer endpoints
  browseAgents: (filters = {}) => api.get('/agents/browse', { params: filters }),
  requestAgentDeposit: (data) => api.post('/deposits/request', data),
  requestAgentWithdrawal: (data) => api.post('/withdrawals/request', data),
  getMyDepositRequests: () => api.get('/deposits/my-requests'),
  getMyWithdrawalRequests: () => api.get('/withdrawals/my-requests'),
  removeContact: (contactId) => api.delete(`/user/contacts/${contactId}`),
  buyVoucher: (currency, value, expiryDays = 365) => api.post('/user/vouchers/buy', { currency, value, expiry_days: expiryDays }),
  getPredictions: (status = 'open') => api.get(`/predictions?status=${status}`),
  getPrediction: (id) => api.get(`/predictions/${id}`),
  placeBet: (predictionId, option, amount) => api.post(`/predictions/${predictionId}/bet`, { option, amount }),
  getMyBets: () => api.get('/user/bets'),
  listTickets: (status = null) => api.get(`/user/support/tickets${status ? `?status=${status}` : ''}`),
  createTicket: (data) => api.post('/user/support/tickets', data),
  getTicket: (id) => api.get(`/user/support/tickets/${id}`),
  replyTicket: (id, message) => api.post(`/user/support/tickets/${id}/messages`, { message }),
  closeTicket: (id) => api.put(`/user/support/tickets/${id}/close`),
  // Smart Growth Savings
  listSavingsPlans: () => api.get('/user/savings/plans'),
  listSavingsAccounts: () => api.get('/user/savings/accounts'),
  getSavingsSummary: () => api.get('/user/savings/summary'),
  getSavingsAccount: (id) => api.get(`/user/savings/accounts/${id}`),
  openSavingsAccount: (data) => api.post('/user/savings/accounts', data),
  depositSavings: (id, amount) => api.post(`/user/savings/accounts/${id}/deposit`, { amount }),
  withdrawSavings: (id, amount) => api.post(`/user/savings/accounts/${id}/withdraw`, { amount }),
  cancelSavingsWithdrawal: (wid) => api.post(`/user/savings/withdrawals/${wid}/cancel`),
  // Providers (public but auth ok)
  getVoucherProviders: () => api.get('/public/voucher-providers'),
  getMobileMoneyProviders: (currency) => api.get(`/public/mobile-money-providers${currency ? `?currency=${currency}` : ''}`),
  getCryptoDepositWallets: (currency) => api.get(`/public/crypto-deposit-wallets${currency ? `?currency=${currency}` : ''}`),
  getCryptoDepositMeta: () => api.get('/public/crypto-deposit-meta'),
  submitCryptoDeposit: (data) => api.post('/user/deposit/crypto', data),
  // Wallet engine (custodial crypto)
  getCustodialAddresses: () => api.get('/user/wallet-engine/addresses'),
  // Community Savings (Stokvel)
  listPublicGroups: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.type) qs.set('type', params.type);
    return api.get(`/groups${qs.toString() ? `?${qs}` : ''}`);
  },
  listMyGroups: () => api.get('/user/groups'),
  createGroup: (data) => api.post('/groups', data),
  getGroup: (id) => api.get(`/groups/${id}`),
  joinGroup: (id, joinCode) => api.post(`/groups/${id}/join`, joinCode ? { join_code: joinCode } : {}),
  leaveGroup: (id) => api.post(`/groups/${id}/leave`, {}),
  listMembers: (id) => api.get(`/groups/${id}/members`),
  contribute: (id, amount) => api.post(`/groups/${id}/contribute`, { amount }),
  listContributions: (id) => api.get(`/groups/${id}/contributions`),
  listProposals: (id) => api.get(`/groups/${id}/proposals`),
  createProposal: (id, data) => api.post(`/groups/${id}/proposals`, data),
  voteProposal: (pid, vote) => api.post(`/proposals/${pid}/vote`, { vote }),
  groupAudit: (id) => api.get(`/groups/${id}/audit`),
  // Tasking & Rewards
  getTaskingConfig: () => api.get('/public/tasking/config'),
  getTaskingDashboard: () => api.get('/user/tasking/dashboard'),
  listTaskingPacks: () => api.get('/user/tasking/packs'),
  buyTaskingPack: (pid) => api.post(`/user/tasking/packs/${pid}/buy`, {}),
  listTaskingTasks: () => api.get('/user/tasking/tasks'),
  completeTask: (tid, payload = {}) => api.post(`/user/tasking/tasks/${tid}/complete`, payload),
  taskingHistory: () => api.get('/user/tasking/history'),
  taskingWithdraw: (coins, currency) => api.post('/user/tasking/withdraw', { coins, currency }),
  // Direct Messaging
  listConversations: () => api.get('/user/conversations'),
  createConversation: (data) => api.post('/user/conversations', data),
  listMessages: (cid) => api.get(`/user/conversations/${cid}/messages`),
  sendMessage: (cid, text, photo_url = null) => api.post(`/user/conversations/${cid}/messages`, { text, photo_url }),
  editMessage: (mid, text) => api.put(`/user/messages/${mid}`, { text }),
  deleteMessage: (mid) => api.delete(`/user/messages/${mid}`),
  reactMessage: (mid, emoji) => api.post(`/user/messages/${mid}/reactions`, { emoji }),
  markConversationRead: (cid) => api.post(`/user/conversations/${cid}/read`, {}),
  addGroupMember: (cid, userId) => api.post(`/user/conversations/${cid}/members`, { user_id: userId }),
  removeGroupMember: (cid, userId) => api.delete(`/user/conversations/${cid}/members/${userId}`),
  leaveGroup: (cid) => api.post(`/user/conversations/${cid}/leave`, {}),
  sendTyping: (cid, isTyping) => api.post(`/user/conversations/${cid}/typing`, { is_typing: isTyping }),
  getUserVouchers: () => api.get('/user/vouchers'),
  blockUser: (uid) => api.post(`/user/block/${uid}`, {}),
  unblockUser: (uid) => api.post(`/user/unblock/${uid}`, {}),
  listBlocked: () => api.get('/user/blocked'),
  reportConversation: (cid, data) => api.post(`/user/conversations/${cid}/report`, data),
  getMessagePrivacy: () => api.get('/user/message-privacy'),
  updateMessagePrivacy: (data) => api.put('/user/message-privacy', data),
};

export const publicAPI = {
  // Generic methods
  get: (url, config) => api.get(url, config),
  post: (url, data, config) => api.post(url, data, config),
  // Specific methods
  getCurrencies: (enabledOnly = true) => api.get(`/currencies?enabled_only=${enabledOnly}`),
  getExchangeRates: (enabledOnly = true) => api.get(`/exchange-rates?enabled_only=${enabledOnly}`),
  getProducts: (enabledOnly = true, skip = 0, limit = 50) => api.get(`/shop/products?enabled_only=${enabledOnly}&skip=${skip}&limit=${limit}`),
  getLanding: (locale = 'en') => api.get(`/public/landing?locale=${encodeURIComponent(locale)}`),
  getLandingWithFeatures: (locale = 'en') => api.get(`/public/landing-with-features?locale=${encodeURIComponent(locale)}`),
  getLandingTVL: () => api.get('/public/landing/tvl'),
};

// Agent Network API
export const agentAPI = {
  // Agent-side endpoints
  getMyAgentProfile: () => api.get('/agents/me'),
  updateMyProfile: (data) => api.put('/agents/me/profile', data),
  getMyPerformance: () => api.get('/agents/me/performance'),
  getMyFloatWallet: () => api.get('/agents/me/float-wallet'),
  getMyCommissionWallet: () => api.get('/agents/me/commission-wallet'),
  getMyCashBalance: () => api.get('/agents/me/cash-balance'),
  getMyDeposits: (status = null) => api.get('/agents/me/deposits', { params: status ? { status } : {} }),
  getMyWithdrawals: (status = null) => api.get('/agents/me/withdrawals', { params: status ? { status } : {} }),
  confirmDeposit: (depositId) => api.post(`/agent/transactions/deposit/${depositId}/confirm`),
  pickupWithdrawal: (withdrawalId) => api.post(`/agent/transactions/withdrawal/${withdrawalId}/pickup`),
  markWithdrawalPaid: (withdrawalId) => api.post(`/agent/transactions/withdrawal/${withdrawalId}/paid`),
  requestFloatTopup: (amount, notes) => api.post('/agent/float/request', { amount, notes }),
  getMySettlements: () => api.get('/settlement/agent/history'),
  calculateSettlement: (periodStart, periodEnd) => api.get('/settlement/agent/calculate', { params: { period_start: periodStart, period_end: periodEnd } }),
  requestSettlement: (data) => api.post('/settlement/agent/request', data),
};

export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getPendingItems: () => api.get('/admin/pending-items'),
  getUsers: (skip = 0, limit = 100) => api.get(`/admin/users?skip=${skip}&limit=${limit}`),
  createCurrency: (data) => api.post('/admin/currencies', data),
  updateCurrency: (id, data) => api.put(`/admin/currencies/${id}`, data),
  deleteCurrency: (id) => api.delete(`/admin/currencies/${id}`),
  createExchangePair: (data) => api.post('/admin/exchange-pairs', data),
  updateExchangePair: (id, data) => api.put(`/admin/exchange-pairs/${id}`, data),
  deleteExchangePair: (id) => api.delete(`/admin/exchange-pairs/${id}`),
  adjustWallet: (userId, currency, amount, action, reason) => api.post('/admin/wallets/adjust', { user_id: userId, currency, amount, action, reason }),
  freezeWallet: (walletId, freeze = true) => api.put(`/admin/wallets/${walletId}/freeze?freeze=${freeze}`),
  lockWallet: (walletId, lock = true) => api.put(`/admin/wallets/${walletId}/lock?lock=${lock}`),
  getDeposits: (status = 'pending') => api.get(`/admin/deposits?status=${status}`),
  approveDeposit: (transactionId) => api.put(`/admin/deposits/${transactionId}/approve`),
  rejectDeposit: (transactionId, reason = '') => api.put(`/admin/deposits/${transactionId}/reject?reason=${reason}`),
  getWithdrawals: (status = 'pending') => api.get(`/admin/withdrawals?status=${status}`),
  completeWithdrawal: (transactionId, txid = '', notes = '') => api.put(`/admin/withdrawals/${transactionId}/complete?txid=${txid}&notes=${notes}`),
  createVoucher: (data) => api.post('/admin/vouchers', data),
  getVouchers: (skip = 0, limit = 100) => api.get(`/admin/vouchers?skip=${skip}&limit=${limit}`),
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  getOrders: (skip = 0, limit = 100) => api.get(`/admin/orders?skip=${skip}&limit=${limit}`),
  getTransactions: (skip = 0, limit = 100) => api.get(`/admin/transactions?skip=${skip}&limit=${limit}`),
  getAuditLogs: (skip = 0, limit = 100) => api.get(`/admin/audit-logs?skip=${skip}&limit=${limit}`),
  getAllWallets: (skip = 0, limit = 200) => api.get(`/admin/wallets/all?skip=${skip}&limit=${limit}`),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  verifyUser: (userId, verified = true) => api.put(`/admin/users/${userId}/verify`, { verified }),
  getPredictions: () => api.get('/admin/predictions'),
  createPrediction: (data) => api.post('/admin/predictions', data),
  settlePrediction: (id, winningOption) => api.put(`/admin/predictions/${id}/settle`, { winning_option: winningOption }),
  closePrediction: (id) => api.put(`/admin/predictions/${id}/close`),
  deletePrediction: (id) => api.delete(`/admin/predictions/${id}`),
  listSupportTickets: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.priority) qs.set('priority', params.priority);
    if (params.search) qs.set('search', params.search);
    return api.get(`/admin/support/tickets${qs.toString() ? `?${qs}` : ''}`);
  },
  getSupportStats: () => api.get('/admin/support/tickets/stats'),
  getSupportTicket: (id) => api.get(`/admin/support/tickets/${id}`),
  replySupportTicket: (id, message) => api.post(`/admin/support/tickets/${id}/messages`, { message }),
  updateSupportTicket: (id, data) => api.put(`/admin/support/tickets/${id}/status`, data),
  getBackupStats: () => api.get('/admin/backup/stats'),
  downloadBackup: () => api.get('/admin/backup', { responseType: 'blob' }),
  restoreBackup: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/restore?confirm=RESTORE', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Smart Growth Savings
  listSavingsPlans: () => api.get('/admin/savings/plans'),
  createSavingsPlan: (data) => api.post('/admin/savings/plans', data),
  updateSavingsPlan: (id, data) => api.put(`/admin/savings/plans/${id}`, data),
  archiveSavingsPlan: (id) => api.delete(`/admin/savings/plans/${id}`),
  listSavingsAccounts: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.currency) qs.set('currency', params.currency);
    return api.get(`/admin/savings/accounts${qs.toString() ? `?${qs}` : ''}`);
  },
  listSavingsWithdrawals: (status = null) => api.get(`/admin/savings/withdrawals${status ? `?status=${status}` : ''}`),
  approveSavingsWithdrawal: (wid) => api.post(`/admin/savings/withdrawals/${wid}/approve`),
  rejectSavingsWithdrawal: (wid, reason) => api.post(`/admin/savings/withdrawals/${wid}/reject`, { reason }),
  holdSavingsWithdrawal: (wid, reason) => api.post(`/admin/savings/withdrawals/${wid}/hold`, { reason }),
  adjustSavingsAccount: (id, data) => api.post(`/admin/savings/accounts/${id}/adjust`, data),
  freezeSavingsAccount: (id) => api.post(`/admin/savings/accounts/${id}/freeze`),
  runSavingsGrowth: () => api.post('/admin/savings/growth/run'),
  // Deposit / Withdrawal Providers
  listVoucherProviders: () => api.get('/admin/voucher-providers'),
  createVoucherProvider: (data) => api.post('/admin/voucher-providers', data),
  updateVoucherProvider: (id, data) => api.put(`/admin/voucher-providers/${id}`, data),
  deleteVoucherProvider: (id) => api.delete(`/admin/voucher-providers/${id}`),
  listMomoProviders: () => api.get('/admin/mobile-money-providers'),
  createMomoProvider: (data) => api.post('/admin/mobile-money-providers', data),
  updateMomoProvider: (id, data) => api.put(`/admin/mobile-money-providers/${id}`, data),
  deleteMomoProvider: (id) => api.delete(`/admin/mobile-money-providers/${id}`),
  listCryptoDepositWallets: () => api.get('/admin/crypto-deposit-wallets'),
  createCryptoDepositWallet: (data) => api.post('/admin/crypto-deposit-wallets', data),
  updateCryptoDepositWallet: (id, data) => api.put(`/admin/crypto-deposit-wallets/${id}`, data),
  deleteCryptoDepositWallet: (id) => api.delete(`/admin/crypto-deposit-wallets/${id}`),
  // Community Savings Groups
  listGroups: (status = null) => api.get(`/admin/groups${status ? `?status=${status}` : ''}`),
  approveGroup: (id) => api.post(`/admin/groups/${id}/approve`, {}),
  rejectGroup: (id, reason) => api.post(`/admin/groups/${id}/reject`, { reason }),
  suspendGroup: (id) => api.post(`/admin/groups/${id}/suspend`, {}),
  listGroupWithdrawals: (status = 'pending_admin') => api.get(`/admin/groups/withdrawals?status=${status}`),
  approveGroupWithdrawal: (id) => api.post(`/admin/groups/withdrawals/${id}/approve`, {}),
  rejectGroupWithdrawal: (id, reason) => api.post(`/admin/groups/withdrawals/${id}/reject`, { reason }),
  // Tasking Admin
  getTaskingConfig: () => api.get('/public/tasking/config'),
  updateTaskingConfig: (data) => api.put('/admin/tasking/config', data),
  getTaskingOverview: () => api.get('/admin/tasking/overview'),
  listAdminTaskingPacks: () => api.get('/admin/tasking/packs'),
  createTaskingPack: (data) => api.post('/admin/tasking/packs', data),
  updateTaskingPack: (id, data) => api.put(`/admin/tasking/packs/${id}`, data),
  deleteTaskingPack: (id) => api.delete(`/admin/tasking/packs/${id}`),
  listAdminTaskingTasks: () => api.get('/admin/tasking/tasks'),
  createTaskingTask: (data) => api.post('/admin/tasking/tasks', data),
  updateTaskingTask: (id, data) => api.put(`/admin/tasking/tasks/${id}`, data),
  deleteTaskingTask: (id) => api.delete(`/admin/tasking/tasks/${id}`),
  listTaskingCompletions: (status = null) => api.get(`/admin/tasking/completions${status ? `?status=${status}` : ''}`),
  approveCompletion: (cid) => api.post(`/admin/tasking/completions/${cid}/approve`, {}),
  rejectCompletion: (cid, reason) => api.post(`/admin/tasking/completions/${cid}/reject`, { reason }),
  adjustUserCoins: (uid, coins, reason) => api.post(`/admin/tasking/users/${uid}/adjust`, { coins, reason }),
  // Matrise Runners
  getRunnerSettings: () => api.get('/pickup/admin/runner-settings'),
  updateRunnerSettings: (settings) => api.put('/pickup/admin/runner-settings', settings),
  getRunnerCommission: () => api.get('/pickup/runner-commission'),
  getLanding: () => api.get('/admin/landing'),
  updateLanding: (data) => api.put('/admin/landing', data),
  updateLandingTranslations: (locale, translations) => api.put(`/admin/landing/translations/${locale}`, { translations }),
  uploadLandingAsset: (kind, filename, contentType, dataB64) =>
    api.post('/admin/landing/upload', { kind, filename, content_type: contentType, data_b64: dataB64 }),
  // Feature Controls
  getFeatureControls: () => api.get('/admin/feature-controls'),
  toggleFeature: (featureId, enabled, reason = null) => api.put(`/admin/feature-controls/${featureId}`, { enabled, reason }),
  enableAllFeatures: () => api.post('/admin/feature-controls/enable-all'),
  disableAllFeatures: (reason) => api.post('/admin/feature-controls/disable-all', { reason }),
  // Matrise Connect - Social Feed
  getConnectFeed: (feedType = 'for-you', skip = 0, limit = 20) => api.get(`/social/feed/${feedType}?skip=${skip}&limit=${limit}`),
  createPost: (content, category = null, mediaUrl = null, mediaType = null) => api.post('/social/posts', { content, category, media_url: mediaUrl, media_type: mediaType }),
  uploadPostMedia: (mediaType, filename, contentType, dataB64) => api.post('/social/upload-media', { media_type: mediaType, filename, content_type: contentType, data_b64: dataB64 }),
  likePost: (postId) => api.post(`/social/posts/${postId}/like`),
  unlikePost: (postId) => api.delete(`/social/posts/${postId}/unlike`),
  commentOnPost: (postId, content, parentCommentId = null) => api.post(`/social/posts/${postId}/comment`, { content, parent_comment_id: parentCommentId }),
  tipPost: (postId, amount, currency = 'ZAR') => api.post(`/social/posts/${postId}/tip`, { amount, currency }),
  getPostTippers: (postId) => api.get(`/social/posts/${postId}/tippers`),
  followUser: (username) => api.post(`/social/follow/${username}`),
  unfollowUser: (username) => api.delete(`/social/unfollow/${username}`),
  getSuggestedPeople: (limit = 10) => api.get(`/social/suggested-people?limit=${limit}`),
  getUserProfile: (username) => api.get(`/user/profile/${username}`),
  // Moderation & Safety
  reportContent: (targetType, targetId, reason, category, details = null) => api.post('/moderation/report', { target_type: targetType, target_id: targetId, reason, category, details }),
  blockUser: (username) => api.post(`/moderation/block/${username}`),
  unblockUser: (username) => api.delete(`/moderation/unblock/${username}`),
  getBlockedUsers: () => api.get('/moderation/blocked'),
  getReports: (status = null, category = null) => api.get(`/moderation/admin/reports?status=${status || ''}&category=${category || ''}`),
  moderateReport: (reportId, action, durationDays = null, notes = null) => api.post('/moderation/admin/moderate', { report_id: reportId, action, duration_days: durationDays, notes }),
  restoreUser: (userId) => api.post(`/moderation/admin/restore-user/${userId}`),
  // Admin user create / delete
  createUser: (data) => api.post('/admin/users', data),
  deleteUser: (uid) => api.delete(`/admin/users/${uid}`),
  // Admin bulk transaction delete
  previewBulkDeleteTransactions: (params) => {
    const qs = new URLSearchParams();
    if (params.before_date) qs.set('before_date', params.before_date);
    if (params.before_month) qs.set('before_month', params.before_month);
    if (params.before_year) qs.set('before_year', params.before_year);
    if (params.status_in) qs.set('status_in', params.status_in);
    return api.get(`/admin/transactions/preview-bulk-delete?${qs}`);
  },
  bulkDeleteTransactions: (data) => api.delete('/admin/transactions/bulk', { data }),
  // Wallet engine
  getWalletEngineStatus: () => api.get('/admin/wallet-engine/status'),
  updateWalletEngineConfig: (data) => api.put('/admin/wallet-engine/config', data),
  listCustodialWallets: () => api.get('/admin/wallet-engine/wallets'),
  bulkGenerateCustodialWallets: () => api.post('/admin/wallet-engine/generate-all', {}),
  listWatchedTokens: () => api.get('/admin/wallet-engine/watched-tokens'),
  createWatchedToken: (data) => api.post('/admin/wallet-engine/watched-tokens', data),
  updateWatchedToken: (id, data) => api.put(`/admin/wallet-engine/watched-tokens/${id}`, data),
  deleteWatchedToken: (id) => api.delete(`/admin/wallet-engine/watched-tokens/${id}`),
  rescanWalletEngine: (data) => api.post('/admin/wallet-engine/rescan', data || {}),
  revealPrivateKey: (userId, password) => api.get(`/admin/wallet-engine/reveal-private-key/${userId}`, { headers: { 'X-Admin-Password': password } }),
  // Treasury
  getTreasuryOverview: () => api.get('/admin/treasury/overview'),
  listTreasuryPools: () => api.get('/admin/treasury/pools'),
  createTreasuryPool: (data) => api.post('/admin/treasury/pools', data),
  updateTreasuryPool: (id, data) => api.put(`/admin/treasury/pools/${id}`, data),
  deleteTreasuryPool: (id) => api.delete(`/admin/treasury/pools/${id}`),
  transferBetweenPools: (data) => api.post('/admin/treasury/transfer', data),
  listRevenueRules: () => api.get('/admin/treasury/rules'),
  createRevenueRule: (data) => api.post('/admin/treasury/rules', data),
  updateRevenueRule: (id, data) => api.put(`/admin/treasury/rules/${id}`, data),
  deleteRevenueRule: (id) => api.delete(`/admin/treasury/rules/${id}`),
  getTreasuryLedger: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api.get(`/admin/treasury/ledger${qs ? `?${qs}` : ''}`);
  },
};


// Pool-Based Predictions API

// Matrise Pickup API
export const pickupAPI = {
  // Customer endpoints
  createDelivery: (data) => api.post('/pickup/deliveries', data),
  getMyDeliveries: (status = null) => api.get(`/pickup/deliveries/my-deliveries${status ? `?status=${status}` : ''}`),
  completeDelivery: (deliveryId) => api.post(`/pickup/deliveries/${deliveryId}/complete`),
  
  // Driver endpoints
  getAvailableDeliveries: (country = null, international = null) => {
    let url = '/pickup/deliveries/available?';
    if (country) url += `country=${encodeURIComponent(country)}&`;
    if (international !== null) url += `international=${international}`;
    return api.get(url);
  },
  acceptDelivery: (deliveryId) => api.post(`/pickup/deliveries/${deliveryId}/accept`),
  makeCounteroffer: (deliveryId, data) => api.post(`/pickup/deliveries/${deliveryId}/offer`, data),
  getMyDriverJobs: (status = null) => api.get(`/pickup/deliveries/my-jobs${status ? `?status=${status}` : ''}`),
  
  // Shared endpoints
  getDeliveryDetails: (deliveryId) => api.get(`/pickup/deliveries/${deliveryId}`),
  updateStatus: (deliveryId, data) => api.put(`/pickup/deliveries/${deliveryId}/status`, data),
  sendMessage: (deliveryId, message) => api.post(`/pickup/deliveries/${deliveryId}/messages`, { delivery_id: deliveryId, message }),
  getMessages: (deliveryId) => api.get(`/pickup/deliveries/${deliveryId}/messages`),
  rateDelivery: (deliveryId, data) => api.post(`/pickup/deliveries/${deliveryId}/rate`, { delivery_id: deliveryId, ...data }),
  
  // Negotiation endpoints
  getOffers: (deliveryId) => api.get(`/pickup/deliveries/${deliveryId}/offers`),
  acceptOffer: (deliveryId, offerId) => api.put(`/pickup/deliveries/${deliveryId}/offers/${offerId}/accept`),
  rejectOffer: (deliveryId, offerId) => api.put(`/pickup/deliveries/${deliveryId}/offers/${offerId}/reject`),
  
  // Admin endpoints
  adminGetAllDeliveries: (status = null) => api.get(`/pickup/admin/deliveries${status ? `?status=${status}` : ''}`),
  adminGetStats: () => api.get('/pickup/admin/deliveries/stats'),
  
  // Payment endpoints
  getWalletBalances: () => api.get('/pickup/wallet/balances'),
  payForDelivery: (deliveryId) => api.post(`/pickup/deliveries/${deliveryId}/pay`),
  cancelDelivery: (deliveryId) => api.post(`/pickup/deliveries/${deliveryId}/cancel`),
  getPaymentStatus: (deliveryId) => api.get(`/pickup/deliveries/${deliveryId}/payment-status`),
  
  // Driver Earnings
  getDriverEarningsSummary: (params) => api.get(`/pickup/driver/earnings/summary${params ? '?' + params : ''}`),
  getDriverEarningsHistory: (page = 1, limit = 20, currency = null) => api.get(`/pickup/driver/earnings/history?page=${page}&limit=${limit}${currency ? `&currency=${currency}` : ''}`),
  getDriverStats: () => api.get('/pickup/driver/earnings/stats'),
  
  // Fee Configuration
  getFeeConfig: () => api.get('/pickup/admin/pickup/fee-config'),
  updateFeeConfig: (data) => api.put('/pickup/admin/pickup/fee-config', data),
  
  // Status Management Configuration
  getStatusConfig: () => api.get('/pickup/admin/pickup/status-config'),
  updateStatusConfig: (config) => api.put('/pickup/admin/pickup/status-config', config),
  getOverdueAlerts: () => api.get('/pickup/admin/pickup/overdue-alerts'),
  resolveAlert: (alertId) => api.post(`/pickup/admin/pickup/alerts/${alertId}/resolve`),
  adminGetAllJobs: (status = null, limit = 100) => api.get(`/pickup/admin/pickup/all-jobs?${status ? `status=${status}&` : ''}limit=${limit}`),
  adminIntervene: (deliveryId, action) => api.post(`/pickup/admin/pickup/jobs/${deliveryId}/intervene`, action),
  
  // Timeline & Photos
  getTimeline: (deliveryId) => api.get(`/pickup/deliveries/${deliveryId}/timeline`),
  uploadDeliveryPhoto: (deliveryId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/pickup/deliveries/${deliveryId}/upload-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Driver Application & Verification
  submitDriverApplication: (data) => api.post('/pickup/driver-application', data),
  getMyDriverApplication: () => api.get('/pickup/driver-application/my-application'),
  uploadDriverDocuments: (applicationId, formData) => api.post(`/pickup/driver-application/upload-documents?application_id=${applicationId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  adminGetDriverApplications: (status = null) => api.get(`/pickup/admin/pickup/driver-applications${status ? `?status=${status}` : ''}`),
  adminReviewDriverApplication: (applicationId, data) => api.put(`/pickup/admin/pickup/driver-application/${applicationId}/review`, data),
  adminGetAllDrivers: (verifiedOnly = null) => api.get(`/pickup/admin/pickup/drivers${verifiedOnly !== null ? `?verified_only=${verifiedOnly}` : ''}`)
};

export { poolPredictionsAPI, poolAdminAPI } from './poolPredictionsAPI';
